import type { JSX } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router";

import { Button } from "../ui/button.js";
import { ErrorState } from "../data/ErrorState.js";

/**
 * Route-level `errorElement`. Replaces react-router's bare-bones default error
 * screen with a branded, recoverable layout. Wired on a pathless route inside
 * the AppShell so the sidebar/topbar stay put and only the content region is
 * swapped when a page throws (render error, lazy-chunk failure, loader reject).
 */
export function RouteErrorBoundary(): JSX.Element {
  const error = useRouteError();
  const navigate = useNavigate();

  const { title, description } = describe(error);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center p-6">
      <ErrorState
        title={title}
        description={description}
        onRetry={() => window.location.reload()}
        className="w-full max-w-md"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-3"
        onClick={() => navigate("/dashboard")}
      >
        Back to dashboard
      </Button>
    </div>
  );
}

function describe(error: unknown): { title: string; description?: string } {
  if (isRouteErrorResponse(error)) {
    return error.status === 404
      ? { title: "Page not found", description: "This page doesn’t exist or has moved." }
      : { title: `${error.status} ${error.statusText}`, description: error.data?.message };
  }
  if (error instanceof Error) {
    return { title: "Something went wrong", description: error.message };
  }
  return { title: "Something went wrong", description: "An unexpected error occurred." };
}
